import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { ArrowRight, Menu, X, Waves, Wrench, Sparkles, Fish, Phone, Mail, MapPin } from "lucide-react";
import reefImg from "@/assets/acquaria-project-reef.jpg";
import lobbyImg from "@/assets/acquaria-project-lobby.jpg";
import yachtImg from "@/assets/acquaria-project-yacht.jpg";
import biotopeImg from "@/assets/acquaria-project-biotope.jpg";

export const Route = createFileRoute("/demo/acquaria-locarno")({
  head: () => ({
    meta: [
      { title: "Acquaria Locarno — Acquari di lusso su misura | Demo FlashWeb" },
      { name: "description", content: "Acquaria progetta, costruisce e mantiene acquari di lusso su misura per ville, hotel e spazi privati in Ticino e nella Svizzera italiana." },
      { name: "robots", content: "noindex, nofollow" },
    ],
    links: [
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      { rel: "stylesheet", href: "https://fonts.googleapis.com/css2?family=Geist:wght@300;400;500;600;700&display=swap" },
    ],
  }),
  component: AcquariaPage,
});

const VIDEO_SRC = "https://d8j0ntlcm91z4.cloudfront.net/user_38xzZboKViGWJOttwIXH07lWA1P/hf_20260622_204221_5339e40b-e73d-4ab0-9c65-79c18c66fd50.mp4";

const WA_HREF = "https://wa.me/41782369200?text=" + encodeURIComponent("Ciao Acquaria, vorrei un preventivo per un acquario su misura.");

function AcquariaPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { label: "Home", href: "#home" },
    { label: "Progetti", href: "#projects" },
    { label: "Studio", href: "#studio" },
    { label: "Contatti", href: "#contact" },
  ];

  return (
    <div style={{ fontFamily: "'Geist', sans-serif", WebkitFontSmoothing: "antialiased", MozOsxFontSmoothing: "grayscale" }} className="bg-black text-white">
      <style>{`
        @keyframes acqFadeSlideUp {
          from { opacity: 0; transform: translateY(24px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .acq-fade { animation: acqFadeSlideUp 0.9s cubic-bezier(0.22,1,0.36,1) both; }
      `}</style>

      {/* ============ HERO ============ */}
      <section id="home" className="relative h-screen w-full overflow-hidden bg-black">
        {/* Background video */}
        <video
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 h-full w-full object-cover"
          style={{ objectPosition: "70% center" }}
          poster=""
        >
          <source src={VIDEO_SRC} type="video/mp4" />
        </video>
        {/* Subtle dark wash for legibility */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/70" />

        {/* Navbar */}
        <nav className="relative z-30 flex items-center justify-between px-6 py-5 md:px-12 lg:px-16">
          <div className="flex items-center gap-10">
            <a href="#home" className="text-lg font-semibold tracking-tight text-white sm:text-xl">
              Acquaria<span className="text-white/40">.ch</span>
            </a>
            <div className="hidden items-center gap-8 md:flex">
              {navLinks.map((l) => (
                <a key={l.href} href={l.href} className="text-sm text-white/80 transition-colors hover:text-white">
                  {l.label}
                </a>
              ))}
            </div>
          </div>

          <a
            href={WA_HREF}
            target="_blank"
            rel="noreferrer"
            className="hidden rounded-lg bg-white px-5 py-2 text-sm font-medium text-black transition-transform hover:scale-105 md:inline-block"
          >
            Parliamone
          </a>

          <button
            onClick={() => setMobileMenuOpen((v) => !v)}
            className="relative z-50 flex h-10 w-10 items-center justify-center text-white transition-transform active:scale-90 md:hidden"
            aria-label="Toggle menu"
          >
            <Menu
              size={22}
              className={`absolute transition-all duration-300 ${mobileMenuOpen ? "rotate-90 scale-50 opacity-0" : "rotate-0 scale-100 opacity-100"}`}
            />
            <X
              size={22}
              className={`absolute transition-all duration-300 ${mobileMenuOpen ? "rotate-0 scale-100 opacity-100" : "-rotate-90 scale-50 opacity-0"}`}
            />
          </button>
        </nav>

        {/* Mobile menu */}
        <div
          className={`absolute inset-x-0 top-0 z-20 overflow-hidden bg-black/95 backdrop-blur-xl transition-all duration-500 ${
            mobileMenuOpen ? "h-screen opacity-100" : "pointer-events-none h-0 opacity-0"
          }`}
          style={{ transitionTimingFunction: "cubic-bezier(0.16,1,0.3,1)" }}
        >
          <div
            className={`flex h-full flex-col justify-center px-8 transition-all delay-100 duration-500 ${
              mobileMenuOpen ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
            }`}
          >
            <div className="flex flex-col gap-6">
              {navLinks.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-3xl font-medium text-white/90 transition-colors hover:text-white"
                >
                  {l.label}
                </a>
              ))}
              <a
                href={WA_HREF}
                target="_blank"
                rel="noreferrer"
                onClick={() => setMobileMenuOpen(false)}
                className="mt-6 inline-flex w-fit items-center gap-2 rounded-full bg-white px-8 py-3.5 text-base font-medium text-black transition-transform hover:scale-105"
              >
                Parliamone <ArrowRight size={16} />
              </a>
            </div>
          </div>
        </div>

        {/* Hero content */}
        <div className="relative z-10 flex h-[calc(100vh-80px)] flex-col justify-between px-6 pb-10 pt-12 sm:pb-12 sm:pt-16 md:px-12 md:pb-16 md:pt-20 lg:px-16">
          <div className="max-w-3xl">
            <div className="mb-4 text-xs text-white/90 sm:mb-6 sm:text-sm acq-fade" style={{ animationDelay: "0.2s" }}>
              <span className="mr-2 inline-block h-1.5 w-1.5 translate-y-[-2px] rounded-full bg-cyan-300 align-middle shadow-[0_0_12px_rgba(103,232,249,0.9)]" />
              Acquari di lusso · Locarno
            </div>
            <h1
              className="text-3xl font-medium leading-[1.1] tracking-tight text-white acq-fade sm:text-5xl md:text-6xl lg:text-7xl"
              style={{ animationDelay: "0.4s" }}
            >
              Diamo forma <br />
              al silenzio dell'acqua, <br />
              una luce alla volta.
            </h1>
          </div>

          <div>
            <p
              className="mb-5 max-w-sm text-sm leading-relaxed text-white/70 acq-fade sm:mb-6 sm:max-w-lg sm:text-base md:text-lg"
              style={{ animationDelay: "0.7s" }}
            >
              Acquari su misura per ville, hotel e spazi privati sul Lago Maggiore.
              Design, ingegneria e manutenzione a regola d'arte — firmate Acquaria.
            </p>
            <a
              href="#projects"
              className="inline-flex items-center gap-2 rounded-lg bg-white px-5 py-2.5 text-sm font-medium text-black transition-transform hover:scale-105 acq-fade sm:px-6 sm:py-3"
              style={{ animationDelay: "0.9s" }}
            >
              Esplora i progetti <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>

      {/* ============ SERVICES ============ */}
      <section id="studio" className="relative px-6 py-24 md:px-12 lg:px-16">
        <div className="mx-auto max-w-6xl">
          <div className="mb-14 max-w-2xl">
            <div className="mb-4 text-xs uppercase tracking-[0.3em] text-cyan-300/80">Servizi</div>
            <h2 className="text-4xl font-medium tracking-tight sm:text-5xl">
              Un ecosistema, <span className="text-white/40">progettato come un'opera.</span>
            </h2>
            <p className="mt-6 text-base text-white/60">
              Dalla prima visione al pesce che nuota: ogni acquario Acquaria è un progetto
              architettonico unico, costruito su misura nel nostro atelier di Locarno.
            </p>
          </div>

          <div className="grid gap-px overflow-hidden rounded-2xl bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { Icon: Waves, title: "Acquari su misura", body: "Vasche marine, dolci e biotopo. Dimensioni e forme illimitate. Vetro extra-chiaro Optiwhite." },
              { Icon: Sparkles, title: "Design & Aquascaping", body: "Composizioni naturali, hardscape giapponese, illuminazione cinematica. Estetica da galleria." },
              { Icon: Wrench, title: "Ingegneria & Filtraggio", body: "Sistemi a sump silenziosi, automazione completa, controllo remoto via app. Affidabilità svizzera." },
              { Icon: Fish, title: "Selezione fauna", body: "Pesci e coralli da allevatori certificati. Quarantena, ambientamento e tracciabilità totale." },
              { Icon: Sparkles, title: "Manutenzione Premium", body: "Contratti mensili o settimanali con tecnici dedicati. Il vostro acquario sempre perfetto." },
              { Icon: Waves, title: "Hotel & Yacht", body: "Installazioni per hospitality di lusso e imbarcazioni. Conformità tecnica e impatto visivo." },
            ].map(({ Icon, title, body }) => (
              <div key={title} className="group bg-black p-8 transition-colors hover:bg-cyan-950/30">
                <Icon className="mb-6 h-7 w-7 text-cyan-300 transition-transform group-hover:scale-110" strokeWidth={1.4} />
                <h3 className="text-lg font-medium">{title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-white/55">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ PROJECTS ============ */}
      <section id="projects" className="relative px-6 py-24 md:px-12 lg:px-16">
        <div className="mx-auto max-w-6xl">
          <div className="mb-14 flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-end">
            <div className="max-w-xl">
              <div className="mb-4 text-xs uppercase tracking-[0.3em] text-cyan-300/80">Selected work</div>
              <h2 className="text-4xl font-medium tracking-tight sm:text-5xl">
                Installazioni recenti.
              </h2>
            </div>
            <p className="max-w-sm text-sm text-white/55">
              Una selezione di progetti realizzati tra il Ticino, il Lago di Como e la Costa Azzurra.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            {[
              { tag: "Villa privata · Ascona", title: "Reef 3200L", desc: "Acquario marino panoramico con barriera corallina viva. Vetro Optiwhite curvato 19mm.", img: reefImg },
              { tag: "Boutique Hotel · Locarno", title: "Lobby Wall 5m", desc: "Parete d'acqua dolce piantumata. Illuminazione dinamica sincronizzata al check-in.", img: lobbyImg },
              { tag: "Yacht · Lago Maggiore", title: "Cabin Aquarium 800L", desc: "Sistema anti-vibrazione, autonomia 72h, gestione remota via satellite.", img: yachtImg },
              { tag: "Residenza · Lugano", title: "Biotopo Amazzonia", desc: "Riproduzione fedele del Rio Negro. Discus selezionati e legni di Mopani originali.", img: biotopeImg },
            ].map((p) => (
              <article
                key={p.title}
                className="group relative aspect-[4/3] overflow-hidden rounded-2xl bg-cyan-950"
              >
                <div
                  className="absolute inset-0 bg-cover bg-center transition-transform duration-[1500ms] group-hover:scale-110"
                  style={{
                    backgroundImage:
                      `linear-gradient(180deg, rgba(0,0,0,0) 30%, rgba(0,0,0,0.85) 100%), url('${p.img}')`,
                  }}
                />
                <div className="absolute inset-x-0 bottom-0 p-7">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-cyan-200/80">{p.tag}</div>
                  <h3 className="mt-2 text-2xl font-medium tracking-tight">{p.title}</h3>
                  <p className="mt-2 max-w-md text-sm text-white/70">{p.desc}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ============ STATS / TRUST ============ */}
      <section className="border-y border-white/10 bg-gradient-to-b from-cyan-950/30 to-black px-6 py-20 md:px-12 lg:px-16">
        <div className="mx-auto grid max-w-6xl gap-12 sm:grid-cols-2 lg:grid-cols-4">
          {[
            ["180+", "Acquari installati"],
            ["12", "Anni d'esperienza"],
            ["24/7", "Assistenza tecnica"],
            ["100%", "Garanzia svizzera"],
          ].map(([n, l]) => (
            <div key={l}>
              <div className="text-5xl font-medium tracking-tight text-white sm:text-6xl">{n}</div>
              <div className="mt-2 text-sm text-white/55">{l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ============ CONTACT ============ */}
      <section id="contact" className="px-6 py-24 md:px-12 lg:px-16">
        <div className="mx-auto grid max-w-6xl gap-12 lg:grid-cols-2 lg:items-end">
          <div>
            <div className="mb-4 text-xs uppercase tracking-[0.3em] text-cyan-300/80">Contatti</div>
            <h2 className="text-4xl font-medium tracking-tight sm:text-6xl">
              Iniziamo <br /> il vostro <span className="text-cyan-300">progetto</span>.
            </h2>
            <p className="mt-6 max-w-md text-base text-white/60">
              Sopralluogo gratuito in tutta la Svizzera italiana e Lombardia.
              Risposta garantita entro 24 ore.
            </p>
          </div>
          <div className="space-y-4">
            <a href={WA_HREF} target="_blank" rel="noreferrer" className="flex items-center justify-between rounded-2xl border border-white/10 bg-white/5 p-5 transition-colors hover:bg-white/10">
              <div className="flex items-center gap-4">
                <Phone className="h-5 w-5 text-cyan-300" />
                <div>
                  <div className="text-sm text-white/55">WhatsApp</div>
                  <div className="text-base font-medium">+41 78 236 92 00</div>
                </div>
              </div>
              <ArrowRight className="h-5 w-5 text-white/40" />
            </a>
            <a href="mailto:studio@acquaria.ch" className="flex items-center justify-between rounded-2xl border border-white/10 bg-white/5 p-5 transition-colors hover:bg-white/10">
              <div className="flex items-center gap-4">
                <Mail className="h-5 w-5 text-cyan-300" />
                <div>
                  <div className="text-sm text-white/55">Email</div>
                  <div className="text-base font-medium">studio@acquaria.ch</div>
                </div>
              </div>
              <ArrowRight className="h-5 w-5 text-white/40" />
            </a>
            <div className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/5 p-5">
              <MapPin className="h-5 w-5 text-cyan-300" />
              <div>
                <div className="text-sm text-white/55">Atelier</div>
                <div className="text-base font-medium">Via della Posta 4 · 6600 Locarno</div>
              </div>
            </div>
          </div>
        </div>
      </section>




      {/* ============ TESTIMONIALS ============ */}
      <section className="border-t border-white/10 px-6 py-24 md:px-12 lg:px-16">
        <div className="mx-auto max-w-6xl">
          <div className="mb-14 max-w-2xl">
            <div className="text-xs uppercase tracking-[0.25em] text-cyan-300/80">Clienti</div>
            <h2 className="mt-3 text-3xl md:text-5xl font-light leading-tight">Hanno scelto Acquaria per le loro residenze e hotel.</h2>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {[
              { q: "L'acquario panoramico da 2.400 litri è il fulcro della villa. Manutenzione mensile sempre puntuale, supporto immediato via WhatsApp.", n: "Famiglia Conti", r: "Villa privata · Ascona" },
              { q: "Hanno progettato il reef del nostro lobby seguendoci dall'idea alla messa in opera. Esperienza eccezionale per gli ospiti.", n: "Stefano Lanzi", r: "GM, Hotel Eden Roc · Ascona" },
              { q: "Acquario d'acqua dolce planted con CO₂ automatizzata. Risultato da rivista e zero pensieri per noi.", n: "Studio Medico Riva", r: "Sala d'attesa · Lugano" },
            ].map((t, i) => (
              <figure key={i} className="flex h-full flex-col justify-between rounded-2xl border border-white/10 bg-white/[0.02] p-7">
                <blockquote className="text-base leading-relaxed text-white/85">"{t.q}"</blockquote>
                <figcaption className="mt-6 border-t border-white/10 pt-5">
                  <div className="text-sm text-cyan-300">{t.n}</div>
                  <div className="mt-1 text-xs uppercase tracking-[0.2em] text-white/45">{t.r}</div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* ============ PRESS / CERT ============ */}
      <section className="border-t border-white/10 px-6 py-16 md:px-12 lg:px-16">
        <div className="mx-auto max-w-6xl">
          <div className="mb-8 text-center text-xs uppercase tracking-[0.3em] text-white/40">Partner & certificazioni</div>
          <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-5 text-white/55">
            <span className="text-lg font-light tracking-wide">Red Sea Pro</span>
            <span className="text-white/20">·</span>
            <span className="text-lg font-light italic tracking-wide">ADA Japan</span>
            <span className="text-white/20">·</span>
            <span className="text-lg font-light tracking-wide">Tropic Marin</span>
            <span className="text-white/20">·</span>
            <span className="text-lg font-light italic tracking-wide">Tunze</span>
            <span className="text-white/20">·</span>
            <span className="text-lg font-light tracking-wide">CITES certified</span>
          </div>
        </div>
      </section>


      {/* ============ FOOTER ============ */}
      <footer className="border-t border-white/10 px-6 py-10 md:px-12 lg:px-16">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 text-xs text-white/40 sm:flex-row">
          <div>© {new Date().getFullYear()} Acquaria — Atelier acquari di lusso, Locarno.</div>
          <div>Demo realizzata da <a href="/" className="underline hover:text-white">FlashWeb Agency</a></div>
        </div>
      </footer>
    </div>
  );
}
